import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class CacheService {
  private cacheStore: Map<string, any> = new Map();

  constructor() {
    this.initializeCache();
  }

  /**
   * Initialize cache with default cacheObj structure
   */
  private initializeCache(): void {
    this.cacheStore.set('cacheObj', {
      userMappedVesselGroupList: {}
    });
  }

  /**
   * Set a value in cache with support for nested objects
   * @param key - The cache key
   * @param value - The value to cache (can be any type including nested objects)
   */
  set<T = any>(key: string, value: T): void {
    this.cacheStore.set(key, value);
  }

  /**
   * Get a value from cache
   * @param key - The cache key
   * @returns The cached value or undefined if not found
   */
  get<T = any>(key: string): T | undefined {
    return this.cacheStore.get(key) as T | undefined;
  }

  /**
   * Check if a key exists in cache
   * @param key - The cache key
   * @returns True if key exists, false otherwise
   */
  has(key: string): boolean {
    return this.cacheStore.has(key);
  }

  /**
   * Clear a specific cache entry
   * @param key - The cache key to clear
   */
  clear(key: string): void {
    this.cacheStore.delete(key);
  }

  /**
   * Clear all cache entries
   */
  clearAll(): void {
    this.cacheStore.clear();
  }

  /**
   * Get the size of the cache
   * @returns Number of cache entries
   */
  size(): number {
    return this.cacheStore.size;
  }

  /**
   * Get a nested value from cache using dot notation
   * @param key - The cache key
   * @param path - Dot-separated path to nested property (e.g., 'userMappedCacheList.1')
   * @returns The nested value or undefined if not found
   */
  getNestedValue<T = any>(key: string, path: string): T | undefined {
    const cacheValue = this.cacheStore.get(key);
    if (!cacheValue) {
      return undefined;
    }

    const keys = path.split('.');
    let value: any = cacheValue;

    for (const k of keys) {
      if (value === null || value === undefined) {
        return undefined;
      }
      value = value[k];
    }

    return value as T | undefined;
  }

  /**
   * Set a nested value in cache using dot notation
   * @param key - The cache key
   * @param path - Dot-separated path to nested property (e.g., 'userMappedCacheList.1')
   * @param value - The value to set
   */
  setNestedValue<T = any>(key: string, path: string, value: T): void {
    let cacheValue = this.cacheStore.get(key);

    if (!cacheValue) {
      cacheValue = {};
      this.cacheStore.set(key, cacheValue);
    }

    const keys = path.split('.');
    let current: any = cacheValue;

    for (let i = 0; i < keys.length - 1; i++) {
      const k = keys[i];
      if (!(k in current)) {
        current[k] = {};
      }
      current = current[k];
    }

    current[keys[keys.length - 1]] = value;
  }
}

// Usage Example
// // Inject the service
// constructor(private cacheService: CacheService) {}

// // Set nested cache like you requested
// this.cacheService.set('cacheObj', {
//   userMappedCacheList: { 1: [] }
// });

// // Or use setNestedValue for convenience
// this.cacheService.setNestedValue('cacheObj', 'userMappedCacheList.1', []);

// // Get values
// const data = this.cacheService.get('cacheObj');
// const nestedData = this.cacheService.getNestedValue('cacheObj', 'userMappedCacheList.1');

// // Clear
// this.cacheService.clear('cacheObj');
// this.cacheService.clearAll();